import type { QcResultRead, RecordingMode } from "../api/types";
import { formatNumber, formatPct } from "../lib/format";
import { InfoTip } from "./Tooltip";

interface Props {
  qc: QcResultRead | null;
  mode: RecordingMode;
}

type Tile = { label: string; value: string; tip?: string };

// Tidy metric tiles for the QC detail. Mode-aware: mode1 has no pose/tracking dimension.
// Key metrics carry an (i) tooltip explaining what each number means.
export default function QcPanel({ qc, mode }: Props) {
  if (!qc) return <div className="card qc-card empty">No QC results yet.</div>;

  const isMode1 = mode === "mode1";
  const topicCount = Object.keys(qc.mcap_topics ?? {}).length;

  const tiles: Tile[] = [
    { label: "Frames", value: formatNumber(qc.frame_count, 0) },
    {
      label: "FPS (mean)",
      value: formatNumber(qc.frame_fps_mean, 1),
      tip: "Mean RGB frame rate; the rig targets ~30 fps.",
    },
    {
      label: "Long gaps",
      value: formatNumber(qc.frame_long_gaps, 0),
      tip: "Frames arriving late enough to count as a dropped/late gap (each costs score).",
    },
    {
      label: "IMU rate",
      value: qc.imu_rate_hz != null ? `${formatNumber(qc.imu_rate_hz, 0)} Hz` : "—",
      tip: "Accelerometer/gyro sample rate; the rig targets ~500 Hz for tight motion fusion.",
    },
    {
      label: "Gravity |a|",
      value: qc.imu_gravity_mean != null ? `${formatNumber(qc.imu_gravity_mean, 2)} m/s²` : "—",
      tip: "Mean accelerometer magnitude at rest; should sit near 9.81 m/s² (Earth gravity). Large drift = mis-scaled IMU.",
    },
    {
      label: "Hand rate",
      value: formatPct(qc.hand_detection_rate, 0),
      tip: "Share of sampled frames with at least one detected hand — proxy for manipulation content.",
    },
    {
      label: "Both hands",
      value: formatPct(
        typeof (qc.metrics_json ?? {})["hand_both_rate"] === "number"
          ? ((qc.metrics_json ?? {})["hand_both_rate"] as number)
          : null,
        0,
      ),
      tip: "Share of sampled frames where BOTH hands are detected — the acceptance criterion buyers score clips on.",
    },
  ];

  if (!isMode1) {
    tiles.push({
      label: "Tracking",
      value: formatPct(qc.tracking_pct != null ? qc.tracking_pct / 100 : null, 1),
      tip: "Share of frames where camera tracking held a valid pose. Low = lost/relocalizing tracker.",
    });
    tiles.push({
      label: "Trajectory",
      value: qc.trajectory_path_m != null ? `${formatNumber(qc.trajectory_path_m, 2)} m` : "—",
      tip: "Total path length the camera travelled over the take (integrated 6-DOF pose).",
    });
  }

  tiles.push({
    label: "Worst sync IMU",
    value: qc.sync_worst_imu_ms != null ? `${formatNumber(qc.sync_worst_imu_ms, 2)} ms` : "—",
    tip: "Largest time gap between an RGB frame and its nearest IMU sample. Smaller = tighter alignment.",
  });
  if (!isMode1) {
    tiles.push({
      label: "Worst sync pose",
      value: qc.sync_worst_pose_ms != null ? `${formatNumber(qc.sync_worst_pose_ms, 2)} ms` : "—",
      tip: "Largest time gap between an RGB frame and its nearest camera pose (target ≤50 ms).",
    });
  }
  tiles.push({
    label: "MCAP messages",
    value: formatNumber(qc.mcap_message_count, 0),
    tip: "Total messages written to the exported .mcap log across all topics.",
  });
  tiles.push({
    label: "MCAP topics",
    value: String(topicCount),
    tip: "Number of distinct channels in the .mcap (RGB, IMU, pose, tracking state, …).",
  });

  return (
    <section className="card qc-card">
      <div className="card-head">
        <h3>QC metrics</h3>
        <div className="qc-gates">
          <Gate label="QC gate" ok={qc.passed} />
          <Gate label="Conform" ok={qc.conform_passed} />
        </div>
      </div>

      <div className="metric-tiles">
        {tiles.map((t) => (
          <div className="metric-tile" key={t.label}>
            <span className="metric-value">{t.value}</span>
            <span className="metric-label">
              {t.label}
              {t.tip && <InfoTip content={t.tip} />}
            </span>
          </div>
        ))}
      </div>

      {topicCount > 0 && (
        <details className="topics">
          <summary>MCAP topics ({topicCount})</summary>
          <ul className="topic-list">
            {Object.entries(qc.mcap_topics).map(([topic, n]) => (
              <li key={topic}>
                <span className="mono">{topic}</span>
                <span className="muted">{n.toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {qc.conform_errors.length > 0 ? (
        <details className="conform-errors" open>
          <summary>Conformance errors ({qc.conform_errors.length})</summary>
          <ul>
            {qc.conform_errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </details>
      ) : (
        <p className="conform-ok">No conformance errors.</p>
      )}
    </section>
  );
}

function Gate({ label, ok }: { label: string; ok: boolean | null }) {
  const cls = ok == null ? "neutral" : ok ? "good" : "bad";
  const text = ok == null ? "—" : ok ? "PASS" : "FAIL";
  return (
    <span className={`gate ${cls}`}>
      <span className="gate-label">{label}</span>
      <span className="gate-state">{text}</span>
    </span>
  );
}
